import { useEffect, useRef, useState } from "react";
import { Bookmark } from "lucide-react";
import { readerScrollBehavior } from "@/lib/scrollBehavior";
import { getReadingRecord, getSavedResearch, removeReadingRecord, saveReadingRecord, setResearchSaved } from "@/lib/localResearchStorage";

const RESUME_MINIMUM = 8;
const COMPLETION_THRESHOLD = 90;

export const ArticleReaderTools = ({ article, bodyRef }) => {
  const [saved, setSaved] = useState(false);
  const [resumeRecord, setResumeRecord] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const frame = useRef(null);

  useEffect(() => {
    setDismissed(false);
    setSaved(getSavedResearch().includes(article.slug));
    const record = getReadingRecord(article.slug);
    setResumeRecord(record && record.progress >= RESUME_MINIMUM && record.progress < COMPLETION_THRESHOLD ? record : null);

    const persistProgress = () => {
      if (frame.current) return;
      frame.current = window.requestAnimationFrame(() => {
        frame.current = null;
        const body = bodyRef.current;
        if (!body) return;
        const start = body.offsetTop;
        const available = Math.max(body.offsetHeight - window.innerHeight, 1);
        const progress = Math.min(100, Math.max(0, ((window.scrollY - start) / available) * 100));
        if (progress >= 1) saveReadingRecord(article.slug, progress >= 95 ? 100 : progress);
      });
    };

    window.addEventListener("scroll", persistProgress, { passive: true });
    return () => {
      window.removeEventListener("scroll", persistProgress);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, [article.slug, bodyRef]);

  const scrollToProgress = (progress) => {
    const body = bodyRef.current;
    if (!body) return;
    const available = Math.max(body.offsetHeight - window.innerHeight, 1);
    window.scrollTo({ top: body.offsetTop + (available * progress / 100), behavior: readerScrollBehavior() });
    setResumeRecord(null);
  };

  const restart = () => {
    removeReadingRecord(article.slug);
    setResumeRecord(null);
    scrollToProgress(0);
  };

  return <div className="article-reader-tools" data-testid="article-reader-tools">
    <button className={`save-research-button ${saved ? "is-saved" : ""}`} type="button" aria-pressed={saved} onClick={() => setSaved(setResearchSaved(article.slug, !saved).includes(article.slug))} data-testid="save-research-button">
      <Bookmark size={15} fill={saved ? "currentColor" : "none"} aria-hidden="true" data-testid="save-research-icon" />
      <span>{saved ? "Saved" : "Save research"}</span>
    </button>
    {resumeRecord && !dismissed && <aside className="continue-reading-prompt" aria-label="Continue where you left off" data-testid="continue-reading-prompt">
      <div><span data-testid="continue-reading-title">Continue where you left off</span><p data-testid="continue-reading-progress">{resumeRecord.progress}% read on this browser</p></div>
      <div className="continue-reading-actions">
        <button type="button" onClick={() => scrollToProgress(resumeRecord.progress)} data-testid="continue-reading-button">Continue</button>
        <button type="button" onClick={restart} data-testid="restart-reading-button">Restart</button>
        <button type="button" onClick={() => setDismissed(true)} data-testid="dismiss-continue-reading-button">Dismiss</button>
      </div>
    </aside>}
  </div>;
};